import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { Eye, Inbox, Layers } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button, buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { PcmpHoldActionButtons } from '../PcmpHoldActions/PcmpHoldActionButtons';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import { pcmpMisQueueQueryOptions } from '../../api/pcmpMis.api';
import {
  PCMP_MIS_DECIDABLE_TABS,
  PCMP_MIS_EMPTY_MESSAGE,
  PCMP_MIS_STAGING_CHANNEL,
  PCMP_MIS_TAB_LABELS,
} from '../../constants/pcmpMis.constants';
import { PCMP_CHANNEL_LABELS } from '../../constants/pcmpProposal.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import type { PcmpMisFilterController } from '../../hooks/usePcmpMisFilters';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../../hooks/usePcmpRowSelection';
import type {
  PcmpMisQueueCounts,
  PcmpMisQueueItem,
  PcmpMisQueueParams,
} from '../../types/pcmpMis.types';
import { PcmpMisActionButtons } from '../PcmpMisActions/PcmpMisActionButtons';
import { PcmpMisStageDialog, type PcmpMisStageTarget } from '../PcmpMisBatches/PcmpMisStageDialog';
import { PcmpSelectionBar } from '../PcmpBatch/PcmpSelectionBar';
import { pcmpSelectColumn } from '../PcmpQueue/pcmpSelectColumn';
import { getPcmpMisColumns } from './PcmpMisColumns';

interface PcmpMisProposalsViewProps {
  controller: PcmpMisFilterController;
  params: PcmpMisQueueParams;
  counts: PcmpMisQueueCounts | undefined;
}

/** A queue tab's proposals; on the staging tabs MIS picks rows into a batch. */
export function PcmpMisProposalsView({ controller, params, counts }: PcmpMisProposalsViewProps) {
  const router = useRouter();
  const { tab, limit, appliedFilters } = controller;
  const { can } = usePcmpPermissions();
  const canAct = can(PCMP_PERMISSION.MIS, PCMP_PERMISSION.ACT);
  // The row carries no MIS capability: offer the decision on the tabs where it
  // applies to anyone with MIS rights, and let the API answer the rest.
  const canDecide = PCMP_MIS_DECIDABLE_TABS.has(tab) && canAct;
  const stagingChannel = PCMP_MIS_STAGING_CHANNEL[tab];
  const canStage = stagingChannel !== undefined && canAct;

  const selection = usePcmpRowSelection(JSON.stringify([tab, appliedFilters]));
  const [stageTarget, setStageTarget] = useState<PcmpMisStageTarget | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpMisQueueQueryOptions(params),
  );

  useKeepPageInRange(params.page, data?.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpMisQueueItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const rows = useMemo(() => data?.data ?? [], [data]);

  const columns = getPcmpMisColumns<PcmpMisQueueItem>({
    tab: params.tab,
    leading: canStage ? [pcmpSelectColumn<PcmpMisQueueItem>({ selection })] : [],
    renderActions: (item) => (
      <>
        {canDecide ? (
          <PcmpMisActionButtons
            proposalId={item.proposalId}
            refNo={item.refNo}
            notVerified={item.misNotVerified}
            className="flex-nowrap"
          />
        ) : null}
        <PcmpHoldActionButtons
          proposalId={item.proposalId}
          refNo={item.refNo}
          canHold={item.canHold}
          canRelease={item.canRelease}
          hold={item.hold}
          className="flex-nowrap"
        />
        <Link
          to="/pcmp/proposals/$proposalId"
          params={{ proposalId: item.proposalId }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
        >
          <Eye className="size-3.5" />
          View
        </Link>
      </>
    ),
  });

  const total = data?.meta.total ?? 0;
  const stagingAmount =
    stagingChannel === 'MARKETING'
      ? (counts?.marketingStagingAmount ?? 0)
      : (counts?.accountsStagingAmount ?? 0);
  const summary = selection.summarize(total, stagingAmount);
  const isPageSelected =
    rows.length > 0 && rows.every((row) => selection.isSelected(row.proposalId));

  const refNoOf = (proposalId: number) => rows.find((row) => row.proposalId === proposalId)?.refNo;

  const startStage = () => {
    if (!stagingChannel) return;
    setStageTarget({
      channel: stagingChannel,
      count: summary.count,
      amount: summary.amount,
      selection: selection.allMatching
        ? {
            selectAllMatching: true,
            filters: appliedFilters,
            excludedProposalIds: selection.pickedIds,
          }
        : { proposalIds: selection.pickedIds },
    });
  };

  return (
    <>
      {canStage && stagingChannel ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          selectAll={{ total, amount: stagingAmount, isPageSelected }}
          hint={`Tick verified proposals to stage them into a ${PCMP_CHANNEL_LABELS[stagingChannel]} batch.`}
        >
          <Button
            type="button"
            size="sm"
            onClick={startStage}
            disabled={summary.count === 0}
            className="gap-1.5"
          >
            <Layers className="size-3.5" />
            Stage for {PCMP_CHANNEL_LABELS[stagingChannel]}
          </Button>
        </PcmpSelectionBar>
      ) : null}

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle={`Could not load ${PCMP_MIS_TAB_LABELS[tab]}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage={PCMP_MIS_EMPTY_MESSAGE[tab]}
          skeletonRowCount={Math.min(limit, 15)}
          getRowClassName={(item) =>
            canStage && selection.isSelected(item.proposalId) ? 'bg-primary/5' : undefined
          }
          onRowClick={openProposal}
        />
      )}

      {data?.meta && !error ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}

      {stageTarget ? (
        <PcmpMisStageDialog
          target={stageTarget}
          refNoOf={refNoOf}
          onStaged={selection.clear}
          onClose={() => setStageTarget(null)}
        />
      ) : null}
    </>
  );
}
